import React from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import { switchClasses } from "@mui/material/Switch";
import { styled } from "@mui/material/styles";
import {
  useGridApiContext,
  useGridSelector,
  useGridRootProps,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  GridPanelWrapper,
  GridPanelHeader,
  GridPanelContent,
  GridPanelFooter,
} from "@mui/x-data-grid-pro";
import type { GridColDef } from "@mui/x-data-grid-pro";

const ColumnsPanelRoot = styled("div")({
  padding: "8px 0px 8px 8px",
});

const ColumnsPanelRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  padding: "1px 8px 1px 7px",
  [`& .${switchClasses.root}`]: {
    marginRight: theme.spacing(0.5),
  },
}));

/**
 * A drop-in replacement for MUI's own `GridColumnsPanel` (pass as `components.ColumnsPanel`) that
 * omits `hideable: false` columns (e.g. the shared actions column — see ACTIONS_COLUMN_BASE) from
 * the list entirely, rather than merely graying out their toggle.
 *
 * MUI's built-in panel always lists every registered column and only disables the switch for
 * non-hideable ones (see its source: it reads columns from gridColumnDefinitionsSelector with no
 * filtering hook) — the `getTogglableColumns` prop that would let us filter the list itself wasn't
 * added until MUI X v6, and this repo is pinned to v5. This component forks the parts of that v5
 * panel needed to add the filter, so it must be kept in sync with @mui/x-data-grid-pro's version if
 * that package is ever upgraded.
 */
export function FilteredColumnsPanel(props: React.HTMLAttributes<HTMLDivElement>): JSX.Element {
  const apiRef = useGridApiContext();
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const allColumns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const columns = React.useMemo(() => allColumns.filter((column) => column.hideable !== false), [allColumns]);
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const rootProps = useGridRootProps();
  const [searchValue, setSearchValue] = React.useState("");

  const toggleColumn = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: field } = event.target;
    apiRef.current.setColumnVisibility(field, columnVisibilityModel[field] === false);
  };

  const toggleAllColumns = React.useCallback(
    (isVisible: boolean) => {
      if (apiRef.current.unstable_caches.columns.isUsingColumnVisibilityModel) {
        // setColumnVisibilityModel replaces the model wholesale rather than merging it, and any
        // field absent from the new model resolves to visible by default — so a naive model built
        // only from `columns` (the hideable ones) would silently reveal a business-logic-hidden,
        // non-hideable column (e.g. CurbPass's `timeRange` for bus-station "park" accounts) that
        // happens to currently be hidden. Preserve any such column's hidden state explicitly.
        const preservedNonHideable = Object.fromEntries(
          allColumns
            .filter((column) => column.hideable === false && columnVisibilityModel[column.field] === false)
            .map((column) => [column.field, false]),
        );
        apiRef.current.setColumnVisibilityModel({
          ...preservedNonHideable,
          ...Object.fromEntries(columns.map((column) => [column.field, isVisible])),
        });
        return;
      }
      // TODO v6: Remove — legacy fallback for grids not using columnVisibilityModel.
      apiRef.current.updateColumns(columns.map((column) => ({ field: column.field, hide: !isVisible } as GridColDef)));
    },
    [apiRef, allColumns, columns, columnVisibilityModel],
  );

  const handleSearchValueChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  }, []);

  const currentColumns = React.useMemo(() => {
    if (!searchValue) {
      return columns;
    }
    const searchValueToCheck = searchValue.toLowerCase();
    return columns.filter((column) => (column.headerName || column.field).toLowerCase().includes(searchValueToCheck));
  }, [columns, searchValue]);

  React.useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  return (
    <GridPanelWrapper {...props}>
      <GridPanelHeader>
        <rootProps.components.BaseTextField
          label={apiRef.current.getLocaleText("columnsPanelTextFieldLabel")}
          placeholder={apiRef.current.getLocaleText("columnsPanelTextFieldPlaceholder")}
          inputRef={searchInputRef}
          value={searchValue}
          onChange={handleSearchValueChange}
          variant="standard"
          fullWidth
          {...rootProps.componentsProps?.baseTextField}
        />
      </GridPanelHeader>
      <GridPanelContent>
        <ColumnsPanelRoot>
          {currentColumns.map((column) => (
            <ColumnsPanelRow key={column.field}>
              <FormControlLabel
                control={
                  <rootProps.components.BaseSwitch
                    checked={columnVisibilityModel[column.field] !== false}
                    onClick={toggleColumn}
                    name={column.field}
                    size="small"
                    {...rootProps.componentsProps?.baseSwitch}
                  />
                }
                label={column.headerName || column.field}
              />
            </ColumnsPanelRow>
          ))}
        </ColumnsPanelRoot>
      </GridPanelContent>
      <GridPanelFooter>
        <rootProps.components.BaseButton
          onClick={() => toggleAllColumns(false)}
          {...rootProps.componentsProps?.baseButton}
        >
          {apiRef.current.getLocaleText("columnsPanelHideAllButton")}
        </rootProps.components.BaseButton>
        <rootProps.components.BaseButton
          onClick={() => toggleAllColumns(true)}
          {...rootProps.componentsProps?.baseButton}
        >
          {apiRef.current.getLocaleText("columnsPanelShowAllButton")}
        </rootProps.components.BaseButton>
      </GridPanelFooter>
    </GridPanelWrapper>
  );
}
